import { derived, get, writable } from 'svelte/store';
import type { Job } from '$lib/careers/types';
import { type JobFiltersState } from '$lib/stores/Careers/jobFiltersStore';
import { jobsListRequestToQueryString } from '$lib/careers/JobsListRequest';

export const DEFAULT_PAGE_LIMIT = 50;

interface Aggregates {
  total: number;
  jobFunctionCounts: Record<string, number>;
}

export type JobsStore = {
  isLoading: boolean;
  jobs: {
    pages: Job[][];
    currentPage: number;
    pageLimit: number;
  };
  aggregates: Aggregates;
};

export function createJobsListStore() {
  const store = writable<JobsStore>({
    isLoading: false,
    jobs: {
      pages: [] as Job[][],
      currentPage: 1,
      pageLimit: DEFAULT_PAGE_LIMIT
    },
    aggregates: {
      total: 0,
      jobFunctionCounts: {}
    }
  });

  function init(jobs: Job[], aggregates: Aggregates) {
    store.update((state) => {
      state.isLoading = false;
      state.jobs.pages = [jobs];
      state.jobs.currentPage = 1;
      state.aggregates = aggregates;

      return state;
    });
  }

  async function fetchNextPage(filters: JobFiltersState) {
    const hasNextPageValue = get(hasNextPage);
    const { isLoading } = get(store);

    if (!hasNextPageValue || isLoading) {
      return;
    }

    store.update((state) => {
      state.isLoading = true;
      state.jobs.currentPage = state.jobs.currentPage + 1;

      return state;
    });

    const { jobs } = get(store);

    try {
      const data = await fetchJobs(jobs.currentPage, jobs.pageLimit, filters);

      store.update((state) => {
        state.jobs.pages.push(data.currentPage);

        return state;
      });
    } finally {
      store.update((state) => {
        state.isLoading = false;

        return state;
      });
    }
  }

  async function fetchJobs(page: number, pageLimit: number, filters: JobFiltersState) {
    const queryStr = jobsListRequestToQueryString({
      page,
      pageLimit,
      ...filters
    });

    const res = await fetch(`/api/careers/jobs?${queryStr}`);

    return await res.json();
  }

  return {
    ...store,
    init,
    fetchNextPage
  };
}

export const jobsStore = createJobsListStore();

export const jobsByJobFunction = derived(jobsStore, ($store) => {
  return $store.jobs.pages.flat().reduce(
    (jobsByJobFunction, job) => {
      if (!jobsByJobFunction[job.jobFunction]) {
        jobsByJobFunction[job.jobFunction] = [];
      }

      jobsByJobFunction[job.jobFunction].push(job);

      return jobsByJobFunction;
    },
    {} as Record<string, Job[]>
  );
});

export const fetchedJobsLength = derived(jobsStore, ($store) => $store.jobs.pages.flat().length);

export const hasNextPage = derived(
  [jobsStore, fetchedJobsLength],
  ([$jobsStore, $fetchedJobsLength]) => $fetchedJobsLength < $jobsStore.aggregates.total
);
